"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PhotoCarousel = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _inheritsLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/inheritsLoose"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _PhotoCarouselDot = require("../PhotoCarouselDot/PhotoCarouselDot");
var _PhotoCarouselDots = require("../PhotoCarouselDots/PhotoCarouselDots");
var _PhotoCarouselKeyboardInstructions = require("../PhotoCarouselKeyboardInstructions/PhotoCarouselKeyboardInstructions");
var _PhotoCarouselNavControls = require("../PhotoCarouselNavControls/PhotoCarouselNavControls");
var _PhotoCarouselSlide = require("../PhotoCarouselSlide/PhotoCarouselSlide");
var _PhotoCarouselSwipeableContainer = require("../PhotoCarouselSwipeableContainer/PhotoCarouselSwipeableContainer");
var _PropertyCard = require("../PropertyCard/PropertyCard.context");
var _VisuallyHidden = require("../VisuallyHidden/VisuallyHidden");
var _PhotoCarousel = require("./PhotoCarousel.styles");
var _excluded = ["aria-label", "children", "currentMediaIndex", "keyboardInstructions", "keyTargetElement", "onMediaIndexChange", "renderNavigationControls", "shouldAnimateTranslations", "shouldLoop"];
// To trigger a full swipe, the touch event needs to travel at least 10% of the
// width of the property card.
var SWIPING_TOLERANCE = 10;

/**
 * Displays a carousel of photos inside of a `PropertyCard`.
 *
 * To change slides, mouse users can use navigation buttons, which appear on hover.
 * Keyboard users can tab to focus on the carousel and then use the left and right
 * arrow keys. Touchscreen users can swipe.
 *
 * While this component was created specifically for `PropertyCard`, it is exported
 * separately so that consumers don't have to import it if they don't need it.
 *
 * @related ['Carousel', 'PropertyCard']
 * @since 8.66.0
 */
var PhotoCarousel = /*#__PURE__*/function (_Component) {
  (0, _inheritsLoose2.default)(PhotoCarousel, _Component);
  function PhotoCarousel(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this.setUpKeyListeners = function () {
      var keyTargetElement = _this.props.keyTargetElement || _this.containerRef;
      var targetElement = keyTargetElement !== null && keyTargetElement !== void 0 && keyTargetElement.current ? keyTargetElement.current : window;
      targetElement.addEventListener('keydown', _this.handleKeyDown);
    };
    _this.handleBlur = function () {
      _this.setState({
        isFocused: false
      });
      _this.hideInstructions();
    };
    _this.handleFocus = function (e) {
      e.preventDefault();
      _this.setState({
        isFocused: true,
        isInstructionsVisible: true
      });
      _this.instructionsTimeout = setTimeout(_this.hideInstructions, 5000);
    };
    _this.handleKeyDown = function (keyDownEvent) {
      // Using `key` instead of `keyCode`. `keyCode` has been deprecated
      // and is not supported by Reassure for our perf tests.
      // CDS-2472

      var shouldAnimateTranslations = _this.props.shouldAnimateTranslations;
      if (keyDownEvent.key === 'ArrowLeft') {
        keyDownEvent.preventDefault();
        if (shouldAnimateTranslations) {
          _this.translateLeft();
        } else {
          _this.withAnimationDisabled(_this.translateLeft);
        }
      }
      if (keyDownEvent.key === 'ArrowRight') {
        keyDownEvent.preventDefault();
        if (shouldAnimateTranslations) {
          _this.translateRight();
        } else {
          _this.withAnimationDisabled(_this.translateRight);
        }
      }
      if (keyDownEvent.key === 'Enter') {
        keyDownEvent.preventDefault();
        if (typeof _this.context.onClick === 'function') {
          _this.context.onClick(keyDownEvent);
        }
      }
    };
    _this.handleLeftButtonClick = function (e) {
      e.preventDefault();
      e.stopPropagation();
      var shouldAnimateTranslations = _this.props.shouldAnimateTranslations;
      if (shouldAnimateTranslations) {
        _this.translateLeft();
      } else {
        _this.withAnimationDisabled(_this.translateLeft);
      }
    };
    _this.handleRightButtonClick = function (e) {
      e.stopPropagation();
      e.preventDefault();
      var shouldAnimateTranslations = _this.props.shouldAnimateTranslations;
      if (shouldAnimateTranslations) {
        _this.translateRight();
      } else {
        _this.withAnimationDisabled(_this.translateRight);
      }
    };
    _this.handleSwiping = function (swipeEventData) {
      // Number of pixels swiped
      var delta = swipeEventData.deltaX;
      // The percent of the Property Card's width that the touch event has travelled.
      var swipingOffset = PhotoCarousel.getSwipingOffset(delta);
      _this.setState({
        swipingOffset: swipingOffset,
        // turn off animation while swiping or else the slide will lag behind the
        // finger movement
        shouldAnimateTranslations: false
      });
    };
    _this.handleSwipedLeft = function () {
      var callback = _this.shouldTranslateOnSwiped() ? _this.translateRight : function () {};
      _this.setState({
        swipingOffset: 0,
        // turn animation back on to show slide snap into place
        // (unless shouldAnimateTranslations was initially set to false)
        shouldAnimateTranslations: _this.props.shouldAnimateTranslations
      }, callback);
    };
    _this.handleSwipedRight = function () {
      var callback = _this.shouldTranslateOnSwiped() ? _this.translateLeft : function () {};
      _this.setState({
        swipingOffset: 0,
        // turn animation back on to show slide snap into place
        // (unless this.props.shouldAnimateTranslations was initially set to false)
        shouldAnimateTranslations: _this.props.shouldAnimateTranslations
      }, callback);
    };
    _this.handleSwipedUpOrDown = function () {
      // An up/down swipe may still shift the slides a bit so snap them back (CDS-2232)
      _this.setState({
        swipingOffset: 0,
        // turn animation back on to show slide snap into place
        // (unless shouldAnimateTranslations was initially set to false)
        shouldAnimateTranslations: _this.props.shouldAnimateTranslations
      });
    };
    _this.hideInstructions = function () {
      _this.setState({
        isInstructionsVisible: false
      });
      clearTimeout(_this.instructionsTimeout);
      _this.instructionsTimeout = null;
    };
    _this.removeKeyListeners = function () {
      var keyTargetElement = _this.props.keyTargetElement || _this.containerRef;
      var targetElement = keyTargetElement !== null && keyTargetElement !== void 0 && keyTargetElement.current ? keyTargetElement.current : window;
      targetElement.removeEventListener('keydown', _this.handleKeyDown);
    };
    _this.translateLeft = function () {
      var shouldLoop = _this.props.shouldLoop;
      var currentMediaIndex = _this.state.currentMediaIndex;
      var isWrappingToLeft = currentMediaIndex === 0;
      var newIndex;
      if (!isWrappingToLeft) {
        newIndex = currentMediaIndex - 1;
        _this.translateToIndex(newIndex);
      } else if (isWrappingToLeft && shouldLoop) {
        newIndex = _this.getIndexOfLastPhoto();
        _this.translateToIndex(newIndex);
      }
      // if (isWrappingToLeft && !shouldLoop), do nothing.
    };
    _this.translateRight = function () {
      var shouldLoop = _this.props.shouldLoop;
      var currentMediaIndex = _this.state.currentMediaIndex;
      var isWrappingToRight = currentMediaIndex === _this.getIndexOfLastPhoto();
      var newIndex;
      if (!isWrappingToRight) {
        newIndex = currentMediaIndex + 1;
        _this.translateToIndex(newIndex);
      } else if (isWrappingToRight && shouldLoop) {
        newIndex = 0;
        _this.translateToIndex(newIndex);
      }
      // if (isWrappingToRight && !shouldLoop), do nothing.
    };
    _this.state = {
      currentMediaIndex: _this.getCurrentMediaIndex() || 0,
      isFocused: false,
      isInstructionsVisible: false,
      swipingOffset: 0,
      shouldAnimateTranslations: props.shouldAnimateTranslations
    };
    _this.containerRef = /*#__PURE__*/(0, _react.createRef)();
    _this.instructionsTimeout = null;
    return _this;
  }
  var _proto = PhotoCarousel.prototype;
  _proto.componentDidUpdate = function componentDidUpdate(_prevProps, prevState) {
    var isFocused = this.state.isFocused;
    var gainedFocus = !prevState.isFocused && isFocused;
    var lostFocus = prevState.isFocused && !isFocused;
    if (gainedFocus) {
      this.setUpKeyListeners();
    } else if (lostFocus) {
      this.removeKeyListeners();
    }
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    var isFocused = this.state.isFocused;
    if (isFocused) {
      this.removeKeyListeners();
    }
  };
  _proto.getCurrentMediaIndex = function getCurrentMediaIndex() {
    var children = this.props.children;
    var currentMediaIndex = this.props.currentMediaIndex;
    if (currentMediaIndex < 0) {
      currentMediaIndex = 0;
    } else {
      var totalMedia = children && children.length || 0;
      currentMediaIndex = totalMedia > 0 ? currentMediaIndex % totalMedia : 0;
    }
    return currentMediaIndex;
  }

  // Returns the percent of the width (as an integer) of a property card that
  // the touch event has travelled.
  // 350 = average width of a property card
  ;
  PhotoCarousel.getSwipingOffset = function getSwipingOffset(delta) {
    return 100 * (delta / 350);
  };
  _proto.getIndexOfLastPhoto = function getIndexOfLastPhoto() {
    var children = this.props.children;
    return children.length - 1;
  };
  _proto.getIsLeftOfCurrentPhoto = function getIsLeftOfCurrentPhoto(mediaIndex) {
    var currentMediaIndex = this.state.currentMediaIndex;
    var children = this.props.children;
    var isDirectlyToLeft = mediaIndex === currentMediaIndex - 1;
    var isWrappingToLeft = currentMediaIndex === 0 && mediaIndex === this.getIndexOfLastPhoto() && children.length !== 2;
    return isDirectlyToLeft || isWrappingToLeft;
  };
  _proto.getIsRightOfCurrentPhoto = function getIsRightOfCurrentPhoto(mediaIndex) {
    var currentMediaIndex = this.state.currentMediaIndex;
    var isDirectlyToRight = mediaIndex === currentMediaIndex + 1;
    var isWrappingToRight = currentMediaIndex === this.getIndexOfLastPhoto() && mediaIndex === 0;
    return isDirectlyToRight || isWrappingToRight;
  };
  _proto.getMediaPosition = function getMediaPosition(mediaIndex, mediaCount) {
    // We render three media items at a time: the current media item (CENTER), the
    // item to the left (LEFT) and the item to the right (RIGHT)
    var currentMediaIndex = this.state.currentMediaIndex;
    if (mediaIndex === currentMediaIndex) {
      return _constants.PHOTO_CAROUSEL_SLIDE_POSITIONS.CENTER;
    }
    if (this.getIsLeftOfCurrentPhoto(mediaIndex, mediaCount)) {
      return _constants.PHOTO_CAROUSEL_SLIDE_POSITIONS.LEFT;
    }
    if (this.getIsRightOfCurrentPhoto(mediaIndex)) {
      return _constants.PHOTO_CAROUSEL_SLIDE_POSITIONS.RIGHT;
    }
    return null;
  };
  _proto.getSlidesToDisplay = function getSlidesToDisplay() {
    var _this2 = this;
    var _this$props = this.props,
      children = _this$props.children,
      shouldLoop = _this$props.shouldLoop;
    var _this$state = this.state,
      currentMediaIndex = _this$state.currentMediaIndex,
      shouldAnimateTranslations = _this$state.shouldAnimateTranslations;
    return children.map(function (slide, mediaIndex) {
      var position = _this2.getMediaPosition(mediaIndex);
      var slideToDisplay = slide;
      if (position) {
        // If !shouldLoop, touchscreen users trying to swipe
        // past the end should see an empty slide.
        if (!shouldLoop && position === _constants.PHOTO_CAROUSEL_SLIDE_POSITIONS.LEFT && currentMediaIndex === 0 || !shouldLoop && position === _constants.PHOTO_CAROUSEL_SLIDE_POSITIONS.RIGHT && currentMediaIndex === _this2.getIndexOfLastPhoto()) {
          slideToDisplay = null;
        }
        return /*#__PURE__*/_react.default.createElement(_PhotoCarouselSlide.PhotoCarouselSlide, {
          key: slide.key,
          shouldAnimateTranslations: shouldAnimateTranslations,
          slidePosition: position
        }, slideToDisplay);
      }
      return null;
    });
  };
  _proto.translateToIndex = function translateToIndex(newIndex) {
    var onMediaIndexChange = this.props.onMediaIndexChange;
    var isInstructionsVisible = this.state.isInstructionsVisible;
    this.setState({
      currentMediaIndex: newIndex
    });
    if (typeof onMediaIndexChange === 'function') {
      onMediaIndexChange(newIndex);
    }
    if (isInstructionsVisible) {
      this.hideInstructions();
    }
  };
  _proto.shouldTranslateOnSwiped = function shouldTranslateOnSwiped() {
    var swipingOffset = this.state.swipingOffset;
    return Math.abs(swipingOffset) > SWIPING_TOLERANCE;
  };
  _proto.withAnimationDisabled = function withAnimationDisabled(callback) {
    this.setState({
      shouldAnimateTranslations: false
    }, callback);
  };
  _proto.render = function render() {
    var _this$props2 = this.props,
      ariaLabel = _this$props2['aria-label'],
      children = _this$props2.children,
      currentMediaIndexProp = _this$props2['currentMediaIndex'],
      keyboardInstructions = _this$props2.keyboardInstructions,
      keyTargetElement = _this$props2.keyTargetElement,
      onMediaIndexChange = _this$props2.onMediaIndexChange,
      renderNavigationControls = _this$props2.renderNavigationControls,
      shouldAnimateTranslations = _this$props2.shouldAnimateTranslations,
      shouldLoop = _this$props2.shouldLoop,
      rest = (0, _objectWithoutProperties2.default)(_this$props2, _excluded);
    var _this$state2 = this.state,
      currentMediaIndex = _this$state2.currentMediaIndex,
      isInstructionsVisible = _this$state2.isInstructionsVisible,
      swipingOffset = _this$state2.swipingOffset;
    var hasMultipleSlides = _react.Children.count(children) > 1;
    var ariaLabelWithCurrentImage = ariaLabel + ", " + keyboardInstructions + ", image " + (currentMediaIndex + 1) + " of " + children.length;
    var leftButtonDisabled = !shouldLoop && currentMediaIndex === 0;
    var rightButtonDisabled = !shouldLoop && currentMediaIndex >= children.length - 1;

    // Only render the carousel if there's more than one photo
    var componentToRender = !hasMultipleSlides ? children : /*#__PURE__*/_react.default.createElement(_PhotoCarousel.StyledPhotoCarousel, (0, _extends2.default)({
      "aria-label": ariaLabelWithCurrentImage,
      currentMediaIndex: currentMediaIndex,
      isInstructionsVisible: isInstructionsVisible,
      keyTargetElement: keyTargetElement,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      ref: this.containerRef,
      role: "group",
      shouldAnimateTranslations: shouldAnimateTranslations,
      shouldLoop: shouldLoop,
      tabIndex: "0"
    }, rest), renderNavigationControls && renderNavigationControls({
      onClick: this.handleLeftButtonClick,
      disabled: leftButtonDisabled
    }, {
      onClick: this.handleRightButtonClick,
      disabled: rightButtonDisabled
    }), /*#__PURE__*/_react.default.createElement(_PhotoCarouselSwipeableContainer.PhotoCarouselSwipeableContainer, {
      onSwiping: this.handleSwiping,
      onSwipedLeft: this.handleSwipedLeft,
      onSwipedRight: this.handleSwipedRight,
      onSwipedUp: this.handleSwipedUpOrDown,
      onSwipedDown: this.handleSwipedUpOrDown,
      swipingOffset: swipingOffset,
      shouldAnimateTranslations: shouldAnimateTranslations
    }, this.getSlidesToDisplay()), /*#__PURE__*/_react.default.createElement(_PhotoCarouselKeyboardInstructions.PhotoCarouselKeyboardInstructions, {
      "aria-hidden": true
    }, keyboardInstructions), /*#__PURE__*/_react.default.createElement(_PhotoCarouselDots.PhotoCarouselDots, {
      total: children.length,
      currentIndex: currentMediaIndex
    }), /*#__PURE__*/_react.default.createElement(_VisuallyHidden.VisuallyHidden, {
      "aria-live": "polite",
      "aria-atomic": "true"
    }, "Image " + (currentMediaIndex + 1) + " of " + children.length));
    return componentToRender;
  };
  return PhotoCarousel;
}(_react.Component);
exports.PhotoCarousel = PhotoCarousel;
PhotoCarousel.contextType = _PropertyCard.PropertyCardContext;
PhotoCarousel.displayName = 'PhotoCarousel';
PhotoCarousel.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The text that screen readers announce when the carousel receives focus.
   */
  'aria-label': _propTypes.default.string,
  /**
   * The slides you want to display. Usually, this will be an array of
   * `PropertyCard.Photo` components. Each child will be wrapped inside of
   * a `PhotoCarousel.Slide`.
   */
  'children': _propTypes.default.node.isRequired,
  /**
   * The index of the slide you want to display on initial render. Note that
   * `PhotoCarousel` is an uncontrolled component.
   */
  'currentMediaIndex': _propTypes.default.number,
  /**
   * Keyboard users will see this text appear in a badge when the carousel receives focus.
   * It is also announced by screen readers as part of the `aria-label`.
   */
  'keyboardInstructions': _propTypes.default.string,
  /**
   * Optional element to limit scope on where keyboard events are active
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  'keyTargetElement': _propTypes.default.object,
  /**
   * Callback that's executed every time the slide changes. Takes the new slide index as an arg.
   */
  'onMediaIndexChange': _propTypes.default.func,
  /**
   * A render prop that returns the navigation controls. It takes two arguments: props
   * objects for the left and right buttons.
   */
  'renderNavigationControls': _propTypes.default.func,
  /**
   * If true, the sliding animation is enabled.
   */
  'shouldAnimateTranslations': _propTypes.default.bool,
  /**
   * If true, navigating past the last slide will loop you back to the first
   * slide. Similarly, you can loop in the other direction.
   */
  'shouldLoop': _propTypes.default.bool
} : {};
PhotoCarousel.defaultProps = {
  'aria-label': 'property images',
  'currentMediaIndex': 0,
  'keyboardInstructions': 'Use arrow keys to navigate',
  'keyTargetElement': null,
  'onMediaIndexChange': function onMediaIndexChange() {},
  'renderNavigationControls': function renderNavigationControls(leftButtonProps, rightButtonProps) {
    return /*#__PURE__*/_react.default.createElement(_PhotoCarouselNavControls.PhotoCarouselNavControls, {
      leftButtonProps: leftButtonProps,
      rightButtonProps: rightButtonProps
    });
  },
  'shouldAnimateTranslations': true,
  'shouldLoop': true
};
PhotoCarousel.Dot = _PhotoCarouselDot.PhotoCarouselDot;
PhotoCarousel.Dots = _PhotoCarouselDots.PhotoCarouselDots;
PhotoCarousel.KeyboardInstructions = _PhotoCarouselKeyboardInstructions.PhotoCarouselKeyboardInstructions;
PhotoCarousel.NavControls = _PhotoCarouselNavControls.PhotoCarouselNavControls;
PhotoCarousel.Slide = _PhotoCarouselSlide.PhotoCarouselSlide;
PhotoCarousel.SwipeableContainer = _PhotoCarouselSwipeableContainer.PhotoCarouselSwipeableContainer;